/**
 * A tab still running the build from before a release, put onto the new one.
 *
 * Each release renames the app's chunks. A tab opened before it still holds
 * the old names, and the first screen it has not loaded yet asks for a chunk
 * the server no longer has. Vite reports that as `vite:preloadError`; left
 * alone it throws, and the person sees "This page broke while drawing" until
 * they reload (2026-10-15). Reloading for them is Vite's documented answer:
 * the page comes back on the new build, at the same address.
 *
 * Once a minute at most. A chunk missing from the new build too would
 * otherwise reload the tab for ever; the second failure is left to throw, and
 * the crash page says so.
 */

const KEY = "crux.stale-build-reload";
const AT_MOST_EVERY_MS = 60_000;

/** The three parts of the window this uses; `window` itself is one. */
export interface Tab {
  readonly addEventListener: (
    type: "vite:preloadError",
    listener: (event: Event) => void,
  ) => void;
  readonly sessionStorage: Pick<Storage, "getItem" | "setItem">;
  readonly location: Pick<Location, "reload">;
}

export function reloadOnStaleBuild(win: Tab): void {
  win.addEventListener("vite:preloadError", (event) => {
    const now = Date.now();
    const last = Number(win.sessionStorage.getItem(KEY) ?? "0");
    if (now - last < AT_MOST_EVERY_MS) return;
    win.sessionStorage.setItem(KEY, String(now));
    event.preventDefault();
    win.location.reload();
  });
}
